/* The tide stations, loaded once and then good all year.
 *
 * Same shape and same reasoning as $lib/currents.svelte: a static file, fetched
 * lazily and cached by the service worker, so the chart still knows the state
 * of the tide in an anchorage with no signal. 158 stations, 622 kB gzipped.
 */

import {
  nearTideStations,
  tideAt,
  type PackedTides,
  type TideNowAt,
  type TideStation
} from '@gennetay/chart-core/lib/tidestations';
import { sources } from '@gennetay/chart-core/sources';

class TideStations {
  stations = $state<TideStation[]>([]);
  error = $state<string | null>(null);
  #started = false;

  async load(): Promise<void> {
    if (this.#started) return;
    this.#started = true;
    try {
      const r = await sources().fetch('static-data', 'tides-2026.json', {
        signal: AbortSignal.timeout(45_000)
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      this.stations = (await import('@gennetay/chart-core/lib/tidestations')).unpackTides((await r.json()) as PackedTides);
      this.error = null;
    } catch (e) {
      // Retryable: the usual cause is being offline before the worker has ever
      // seen the file, and that fixes itself.
      this.#started = false;
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  /** Every station's state at `t`. Stations past the data drop out. */
  at(t: number): { station: TideStation; now: TideNowAt }[] {
    const out: { station: TideStation; now: TideNowAt }[] = [];
    for (const s of this.stations) {
      const now = tideAt(s, t);
      if (now) out.push({ station: s, now });
    }
    return out;
  }

  /** The [min, max] instant the shipped data covers, once loaded. */
  get range(): [number, number] | null {
    let min = Infinity;
    let max = -Infinity;
    for (const s of this.stations) {
      const f = s.events[0];
      const l = s.events[s.events.length - 1];
      if (f && l) {
        if (f.t < min) min = f.t;
        if (l.t > max) max = l.t;
      }
    }
    return Number.isFinite(min) ? [min, max] : null;
  }

  near(pos: { lat: number; lon: number }, km = 60): TideStation[] {
    return nearTideStations(this.stations, pos, km);
  }
}

export const tideStations = new TideStations();
